import Phaser from 'phaser';

import { GameBehavior } from '../behaviors/game-behavior';
import { LoadingBehavior } from '../behaviors/loading-behavior';
import { GameInterfaceState, UxGameListener } from '../listeners/ux-game-listener';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { GameMainScene } from 'src/games/common/phaser/game/scenes/game-main-scene';
import { ActionBus, GameState, Secret } from 'game-logic';
import { runInInjectionContext, signal } from '@angular/core';
import { GameAlert } from '../common/game-alert';
import { PaidBehavior } from '../behaviors/paid-behavior';
import { UxGameServices } from 'src/app/core/services/ux-game/ux-game-services';
import { GameCompletionHandler } from '../completion-handlers/game-completion-handler';
import { LoadingCompletionHandler } from '../completion-handlers/loading-completion-handler';
import { PaidCompletionHandler } from '../completion-handlers/paid-completion-handler';
import { PracticeBehavior } from '../behaviors/practice-behavior';
import { PracticeCompletionHandler } from '../completion-handlers/practice-completion-handler';
import { GameId } from 'src/app/model/game/game-id';
import { gameIdToName } from 'src/app/model/game/game-id.utils';
import { UxGameServiceAction } from 'src/app/core/services/ux-game/ux-game-service-action';
import { BehaviorSubject } from 'rxjs';

export abstract class UxGameController {
  public abstract get gameId(): GameId;

  get gameName() {
    return gameIdToName(this.gameId, this.services.translocoService);
  }
  get scoreSignal() {
    return this.currentScore.asReadonly();
  }
  get countdownSignal() {
    return this.countdown.asReadonly();
  }
  get bountyAmountSignal() {
    return this.bountyAmount.asReadonly();
  }
  get tournamentCountdownSignal() {
    return this.tournamentCountdown.asReadonly();
  }
  get formattedRewardSignal() {
    return this.formattedReward.asReadonly();
  }
  get opponentSignal() {
    return this.opponent.asReadonly();
  }
  get opponentIdSignal() {
    return this.opponentId.asReadonly();
  }
  get gameInterfaceStateSignal() {
    return this.gameInterfaceState.asReadonly();
  }
  get internetStatus() {
    return this.internetStatusSignal.asReadonly();
  }
  get inputEnabled$() {
    return this.inputEnabledSubject.asObservable();
  }

  private currentScore = signal<number | undefined>(undefined);
  private countdown = signal<number | undefined>(undefined);
  private bountyAmount = signal<number | undefined>(undefined);
  private tournamentCountdown = signal<number | undefined>(undefined);
  private formattedReward = signal<string | undefined>(undefined);
  private opponent = signal<string | undefined>(undefined);
  private opponentId = signal<string | undefined>(undefined);
  private gameInterfaceState = signal(GameInterfaceState.GameLoading);
  private internetStatusSignal = signal<boolean>(true);

  private inputEnabledSubject = new BehaviorSubject<boolean>(true);

  private gameAlert: GameAlert;
  private gameBehavior: GameBehavior;
  private gameCompletionHandler: GameCompletionHandler;
  private currentGameState: GameState = GameState.Init;

  protected constructor(
    private gameListener: UxGameListener,
    private preloadScene: Phaser.Scene,
    private mainScene: GameMainScene,
    private gameServiceActionBus: ActionBus<UxGameServiceAction>,
    private services: UxGameServices,
  ) {
    this.gameAlert = new GameAlert(
      services.profileService.id,
      services.alertController,
      services.analyticsService,
      services.translocoService,
      services.themeService,
      services.modalController,
      services.currencyPipe,
      services.httpService,
      services.router,
      services.platform.is('mobile'),
      services.tournamentService,
    );
    this.gameBehavior = new LoadingBehavior();
    this.gameCompletionHandler = new LoadingCompletionHandler();

    this.gameListener.setSecretFetcher(
      async (indexes, gameHash, disclosedSecrets, existingDeal) =>
        await this.fetchSecrets(indexes, gameHash, disclosedSecrets, existingDeal),
    );

    this.gameListener.gameActions.on('onGameLoaded', () => {
      this.services.analyticsService.log(EventName.ClinchGameLoaded);
    });
    this.gameListener.gameActions.on('onStartGame', async () => {
      // TODO: I don't think we need this
      //this.analyticsService.log(EventName.GameSceneStarted);

      this.gameInterfaceState.set(GameInterfaceState.GameStarting);
      await this.gameBehavior.startRoom();
      this.mainScene.startRound([], this.gameBehavior.logicVersion);
      await this.gameBehavior.onStartNewGame();
      this.gameInterfaceState.set(GameInterfaceState.GameStarted);
    });
    this.gameListener.gameActions.on('onScoreChange', (payload) => {
      this.currentScore.set(payload.score);
    });
    this.gameListener.gameActions.on('onNewMove', async (payload) => {
      this.internetStatusSignal.set(await this.gameBehavior.setMoves(payload.moves));
    });
    this.gameListener.gameActions.on('onGameStateChange', (payload) => {
      this.currentGameState = payload.state;
    });
    this.gameListener.gameActions.on('onGameComplete', async (payload) => {
      this.internetStatusSignal.set(await this.gameBehavior.setMoves(payload.moves));
      this.gameBehavior.onStopGame();

      await this.gameCompletionHandler.endGameComplete(
        {
          roomId: this.gameBehavior.currentRoomId,
          score: this.currentScore() ?? 0,
          moves: this.gameBehavior.getMoves(),
          roundDeadline: this.gameBehavior.roundDeadline,
        },
        payload.isComplete,
        (pause) => this.pauseGame(pause),
      );
    });

    this.gameServiceActionBus.on('resetToLoading', async () => {
      await this.resetToLoading();
    });
    this.gameServiceActionBus.on('launchPracticeGame', async (payload) => {
      await this.launchPracticeGame(payload.rewardCents, payload.entryFeeCents);
    });
    this.gameServiceActionBus.on('launchPaidGame', async (payload) => {
      await this.launchPaidGame(payload.roomId);
    });
  }

  onDestroy(): void {
    this.gameBehavior.onDestroy();
  }

  protected abstract resetGame(): void;

  public abstract setGameVisible(isVisible: boolean): void;

  public allScenes(): Phaser.Scene[] {
    return [this.preloadScene, this.mainScene].filter(
      (obj): obj is Phaser.Scene => obj !== undefined,
    );
  }

  async finishGame(): Promise<void> {
    if (this.currentGameState === GameState.AutoFinish) {
      this.mainScene?.setInstantFinish();
      return;
    } else if (
      this.currentGameState === GameState.EndingAnimation ||
      this.currentGameState === GameState.Finished
    ) {
      return;
    }

    await this.gameCompletionHandler.endGameUserFinish(
      {
        roomId: this.gameBehavior.currentRoomId,
        score: this.currentScore() ?? 0,
        moves: this.gameBehavior.getMoves(),
        roundDeadline: this.gameBehavior.roundDeadline,
      },
      (pause) => this.pauseGame(pause),
    );
  }

  private async resetToLoading(): Promise<void> {
    this.resetGame();

    const loadingBehavior = new LoadingBehavior();
    const loadingCompletionHandler = new LoadingCompletionHandler();

    this.setGameBehavior(loadingBehavior, loadingCompletionHandler);

    this.gameInterfaceState.set(GameInterfaceState.GameLoading);
  }

  private async launchPaidGame(roomId: string): Promise<void> {
    this.resetGame();

    const paidBehavior = new PaidBehavior(roomId, this.services, this.gameAlert);
    const paidCompletionHandler = new PaidCompletionHandler(this.services, this.gameAlert);

    this.setGameBehavior(paidBehavior, paidCompletionHandler);

    this.gameInterfaceState.set(GameInterfaceState.GameWaiting);

    await paidBehavior.initRoom();

    try {
      await paidBehavior.initRoomTournament();
    } catch (error) {
      this.services.analyticsService.handleError(error, 'Fetch Room Tournaments');
    }

    await paidBehavior.createGame();
  }

  private async launchPracticeGame(rewardCents: number, entryFeeCents: number): Promise<void> {
    this.resetGame();

    const practiceBehavior = new PracticeBehavior(this.services);
    const practiceCompletionHandler = new PracticeCompletionHandler(
      this.services,
      this.gameAlert,
      this.gameId,
      rewardCents,
      entryFeeCents,
    );

    this.setGameBehavior(practiceBehavior, practiceCompletionHandler);

    this.gameInterfaceState.set(GameInterfaceState.GameWaiting);

    const rewardDollars = rewardCents / 100;
    const practiceReward = rewardDollars > 0 ? `$${rewardDollars.toFixed(2)}` : undefined;
    const result = await practiceBehavior.createGame(this.gameId, practiceReward);
    practiceCompletionHandler.setOpponentResult(
      result.opponentId,
      result.opponentName,
      result.opponentScore,
    );
  }

  private async fetchSecrets(
    indexes: number[],
    gameHash: string,
    disclosedSecrets: number,
    existingDeal: boolean,
  ): Promise<Secret[]> {
    return await this.gameBehavior.discloseSecrets(
      indexes,
      gameHash,
      disclosedSecrets,
      existingDeal,
    );
  }

  private setGameBehavior(behavior: GameBehavior, completionHandler: GameCompletionHandler): void {
    this.gameBehavior.onDestroy();
    this.gameBehavior = behavior;
    this.gameCompletionHandler = completionHandler;
    this.inputEnabledSubject.next(true);

    runInInjectionContext(this.services.injector, () => {
      this.gameBehavior.formattedReward$.subscribe((value) => {
        queueMicrotask(() => {
          this.formattedReward.set(value ?? undefined);
        });
      });
      this.gameBehavior.opponent$.subscribe((value) => {
        queueMicrotask(() => {
          this.opponent.set(value);
        });
      });
      this.gameBehavior.opponentId$.subscribe((value) => {
        queueMicrotask(() => {
          this.opponentId.set(value);
        });
      });
      this.gameBehavior.bountyAmount$.subscribe((value) => {
        queueMicrotask(() => {
          this.bountyAmount.set(value);
        });
      });
    });

    this.gameBehavior.behaviorActions.on('showStartButton', (payload) => {
      this.mainScene.showStartButton(payload.show);
    });
    this.gameBehavior.behaviorActions.on('autoStartGame', () => {
      this.mainScene.autoStartGame();
    });
    this.gameBehavior.behaviorActions.on('resumeExistingGame', () => {
      this.onResumeExistingGame();
    });
    this.gameBehavior.behaviorActions.on('onCountdownChanged', (payload) => {
      queueMicrotask(() => {
        this.countdown.set(payload.countdown);
      });
    });
    this.gameBehavior.behaviorActions.on('onTournamentCountdownChanged', (payload) => {
      queueMicrotask(() => {
        this.tournamentCountdown.set(payload.tournamentCountdown);
      });
    });
    this.gameBehavior.behaviorActions.on('onDeadlineExpired', async () => {
      if (this.currentGameState === GameState.AutoFinish) {
        this.mainScene.setInstantFinish();
        return;
      } else if (
        this.currentGameState === GameState.EndingAnimation ||
        this.currentGameState === GameState.Finished
      ) {
        return;
      }

      await this.gameCompletionHandler.endGameDeadlineTimeout(
        {
          roomId: this.gameBehavior.currentRoomId,
          score: this.currentScore() ?? 0,
          moves: this.gameBehavior.getMoves(),
          roundDeadline: this.gameBehavior.roundDeadline,
        },
        (pause) => this.pauseGame(pause),
      );
    });
  }

  async onResumeExistingGame(): Promise<void> {
    //this.analyticsService.log(EventName.GameSceneStarted);

    this.gameInterfaceState.set(GameInterfaceState.GameStarting);
    await this.gameBehavior.startRoom();
    const initialMoves = await this.gameBehavior.getInitialMoves();

    this.mainScene.startRound(initialMoves, this.gameBehavior.logicVersion);
    this.gameInterfaceState.set(GameInterfaceState.GameStarted);
  }

  private pauseGame(pause: boolean): void {
    this.inputEnabledSubject.next(!pause);
    this.gameBehavior.onPauseGame(pause);
  }
}
